import API from '@/api';
import get from 'lodash/get';
import EventEmitter from 'eventemitter3';
import isEmpty from 'lodash/isEmpty';
import intersection from 'lodash/intersection';
import FirmPreferenceMixins from './FirmPreferenceMixins';
import UtilsMixins from './UtilsMixins';
import uniq from 'lodash/uniq';
import {
  supportedExtensionsForPreview,
  predefinedQuestionAnswers,
} from '@/constants/variables';

export default {
  mixins: [FirmPreferenceMixins, UtilsMixins],
  data() {
    return {
      supportedExtensionsForPreview,
      predefinedQuestionAnswers,
      documentsToImport: [],
      documentUploading: false,
      documentsDeleting: [],

      // For "Others" document
      currentDocument: null,
      selectOptionsDialogOpened: false,
      selectedOptions: [],
      selectedOtherOptions: false,
      otherOptions: [''],
      eventEmitter: new EventEmitter(),
      otherDocumentsTab: true,

      // For Preview dialog
      previewDialogOpened: false,
      documentPreviewData: null,
      previewRotation: 0,
      selectedDocumentLink: null,
      previewType: null,
      previewLoaded: false,
    };
  },

  computed: {
    currentQuestionnaireOtherDocuments() {
      if (
        this.currentQuestionnaire &&
        this.currentQuestionnaire.extracted_documents
      ) {
        return this.currentQuestionnaire.extracted_documents
          .filter(d => d.marked_as_others)
          .map(d => {
            const list = d.source.split('/');
            list.splice(0, 3);

            return list.join('/');
          });
      } else {
        return [];
      }
    },

    unemptyOtherOptions() {
      return this.otherOptions.filter(option => {
        return !isEmpty(option);
      });
    },

    disableProvidedElsewhere() {
      return get(
        this.currentFirm,
        'preferences.disable_provided_elsewhere',
        false,
      );
    },

    canInsert() {
      return (
        this.selectedOptions.length > 0 ||
        (this.selectedOtherOptions && !isEmpty(this.unemptyOtherOptions))
      );
    },
  },

  methods: {
    get,
    addMoreOptions() {
      this.otherOptions.push('');
    },

    deleteOtherOption(index) {
      this.otherOptions.splice(index, 1);
    },

    cancelSelectOptions() {
      this.selectOptionsDialogOpened = false;
      this.selectedOptions = [];
      this.selectedOtherOptions = false;
      this.otherOptions = [''];
    },

    listDocuments(answer) {
      if (!answer) {
        return [];
      } else {
        return answer
          .split('&!&')
          .filter((item, index) => !(index === 0 && item === ''));
      }
    },

    getDocumentName(document) {
      return document.split('/')[document.split('/').length - 1];
    },

    getParentQuestion(question) {
      return this.allQuestions.find(
        q => q.id === question.follow_up_question_id,
      );
    },

    async previewDocument(doc) {
      this.previewType = '';
      this.previewLoaded = false;
      this.documentPreviewData = '';

      if (doc.data && doc.data instanceof File) {
        this.previewRotation = 0;
        this.previewType = doc.extension;
        this.documentPreviewData = doc.data;
        this.previewDialogOpened = true;
        this.previewLoaded = true;
      } else {
        this.selectedDocumentLink = doc;
        this.previewRotation = 0;
        this.previewType = this.endsWithAny(
          this.supportedExtensionsForPreview,
          doc,
        );

        if (this.previewType) {
          if (this.previewType === 'pdf') {
            this.pdfLoaded = false;
          }

          this.previewDialogOpened = true;

          try {
            const response = await API.integration.downloadDocument({
              companyId: this.currentCompany.id,
              link: this.currentCompany.upload_link.concat('/', doc),
            });

            if (response.data.document) {
              this.documentPreviewData = response.data.document.data.link;
            } else {
              console.log(response.data.error);
              this.$store.dispatch('application/setErrorNotification', {
                message: 'There was a problem downloading the document.',
                errorToSend: response.data.error,
              });
            }
          } catch (e) {
            console.log(e);
            this.$store.dispatch('application/setErrorNotification', {
              message: 'There was a problem downloading the document.',
              errorToSend: e,
            });
          } finally {
            this.previewLoaded = true;
          }
        } else {
          try {
            const response = await API.integration.downloadDocument({
              companyId: this.currentCompany.id,
              link: this.currentCompany.upload_link.concat('/', doc),
            });

            if (response.data.document) {
              this.documentPreviewData = response.data.document.data.link;
              this.downloadDocumentByLink(
                this.documentPreviewData,
                this.getDocumentName(doc),
              );
            } else {
              console.log(response.data.error);
              this.$store.dispatch('application/setErrorNotification', {
                message: 'There was a problem downloading the document.',
                errorToSend: response.data.error,
              });
            }
          } catch (e) {
            console.log(e);
            this.$store.dispatch('application/setErrorNotification', {
              message: 'There was a problem downloading the document.',
              errorToSend: e,
            });
          }
        }
      }
    },

    saveQuestion(question, value) {
      question.answer = value;

      // Clear answer later
      if (isEmpty(question.answer)) {
        question.answer_by = null;
      }

      this.$emit('save-question', question);

      if (question.answer === 'Provided Elsewhere') {
        this.$emit(
          'open-comment-dialog',
          question,
          'Please explain where/how you provided this document (e.g. Email, Mail, In-person).',
          'Provided Elsewhere',
        );
      } else if (
        question.answer === 'Upload Later' ||
        question.answer === 'Answer Later'
      ) {
        this.$emit('open-answer-later-dialog', question);
      }
    },

    async uploadBulkDocument() {
      if (this.bulkUploadProcessor) {
        this.bulkUploadProcessorUploading = true;
      }
      let path;
      if (isEmpty(this.currentQuestionnaire.document_path)) {
        path = '';
      } else {
        path = this.currentQuestionnaire.document_path
          .replace('{year}', this.currentQuestionnaire.tax_year)
          .replace('{section}', 'Bulk Uploads')
          .replace('{questionnaire_name}', this.currentQuestionnaire.name);
      }

      for (const documentToImport of this.documentsToImport) {
        let response;
        try {
          this.documentUploading = false;
          this.currentDocument = documentToImport;
          this.selectedOptions = [];
          if (!this.bulkUploadProcessor) {
            this.selectOptionsDialogOpened = true;
            await this.waitForSelectedOptions();
          }
          this.documentUploading = true;

          const {
            data: { id, upload_link },
          } = await API.integration.getTemporaryUploadLink({
            companyId: this.currentQuestionnaire.company_id,
          });

          await API.integration.uploadToDropbox(
            upload_link,
            documentToImport.data,
          );
          const documentName = documentToImport.data.name;
          const allOptions = this.selectedOtherOptions
            ? [...this.selectedOptions, ...this.unemptyOtherOptions]
            : this.selectedOptions;

          const formatter = new Intl.ListFormat('en', {
            style: 'long',
            type: 'conjunction',
          });
          const description = formatter.format(allOptions);

          const sections = uniq(
            this.allDocumentsNeeded
              .filter(question => {
                return intersection(question.options, allOptions).length > 0;
              })
              .map(question => question.section),
          );

          if (this.selectedOtherOptions) {
            sections.push('Additional Attachments');
          }

          response = await API.integration.processDocument({
            id,
            documentName,
            companyId: this.currentQuestionnaire.company_id,
            path: '/'.concat(path),
            questionnaireId: this.currentQuestionnaire.id,
            documentType: this.bulkUploadProcessor ? 'bulk_document' : null,
            markedAsOthers:
              this.selectedOtherOptions && !isEmpty(this.unemptyOtherOptions)
                ? true
                : false,
            selectedOptions: this.bulkUploadProcessor ? null : allOptions,
            description: this.bulkUploadProcessor ? null : description,
            tags: this.bulkUploadProcessor ? null : sections,
            requiresSplitting: true,
          });

          if (response.data.error) {
            console.log(response.data.error);
            this.$store.dispatch('application/setErrorNotification', {
              message: 'There was a problem uploading the documents.',
              errorToSend: response.data.error,
              userEmail: this.currentUser.email,
            });
            this.bulkUploadProcessorUploading = false;
          } else if (response.data.is_duplicate) {
            this.$store.dispatch('application/setErrorNotification', {
              message: `The document ${documentName} has already been uploaded before. Thus, you don't need to upload it again.`,
            });
            this.bulkUploadProcessorUploading = false;
          } else {
            this.insertDocument(response.data, this.selectedOptions);
            this.$emit('refresh-questionnaire-until-documents-are-processed');

            // Wait for the document to be processed before deactivating the upload spinner
            await this.checkDocumentProcessed(response.data);
            this.bulkUploadProcessorUploading = false;
          }
        } catch (e) {
          console.log(e);
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem uploading the documents.',
            errorToSend: e,
            userEmail: this.currentUser.email,
          });
          this.bulkUploadProcessorUploading = false;
        } finally {
          this.selectOptionsDialogOpened = false;
          this.documentUploading = false;
          this.selectedOptions = [];
          this.selectedOtherOptions = false;
          this.otherOptions = [''];
        }
      }

      this.documentsToImport = [];
    },

    insertDocument(doc, selectedOptions) {
      selectedOptions.forEach(option => {
        const question = this.allDocumentsNeeded.find(question =>
          question.options.includes(option),
        );

        const list = doc.document_location.split('/');
        list.splice(0, 3);
        const answer = question.answer + '&!&'.concat(list.join('/'));

        this.saveQuestion(question, answer);
      });
    },

    async deleteDocument(question, document) {
      this.documentsDeleting.push(
        question ? question.id : 'other' + '-' + document,
      );

      try {
        const response = await API.integration.deletePath({
          path: '/'.concat(document),
          companyId: this.currentCompany.id,
        });

        if (response.data.error) {
          console.log(e);
        } else {
          if (question) {
            let newAnswer;

            if (question.answer.split('&!&').length === 1) {
              newAnswer = '';
            } else {
              newAnswer = question.answer
                .split('&!&')
                .filter(d => d !== document)
                .join('&!&');
            }

            await API.questionnaire.answerQuestion({
              id: question.id,
              answer: newAnswer,
              companyId: this.currentCompany.id,
            });
          }

          this.$emit('refresh-questionnaire', () => {
            console.log('callback');
            this.documentsDeleting = this.documentsDeleting.filter(
              d => d !== question.id + '-' + document,
            );
          });
        }
      } catch (e) {
        console.log(e);
        this.documentsDeleting = this.documentsDeleting.filter(
          d => d !== question.id + '-' + document,
        );
      }
    },

    isDocumentBeingDeleted(questionId, document) {
      return this.documentsDeleting.includes(questionId + '-' + document);
    },

    async deleteExtractedDocument(id) {
      try {
        const response = await API.extractedDocument.deleteExtractedDocument(
          id,
        );
        if (response.data.error) {
          console.log(response.data.error);
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'Successfully deleted.',
          });
          this.currentQuestionnaire.extracted_documents = this.currentQuestionnaire.extracted_documents.filter(
            d => d.id !== id,
          );
        }
      } catch (e) {
        console.log(e);
      }
    },

    endsWithAny(suffixes, string) {
      return suffixes.find(function(suffix) {
        return string.toLowerCase().endsWith(suffix);
      });
    },

    getExtensionOfFilename(filename) {
      return filename.split('.').pop();
    },

    getNameWithoutExtensionOfFilename(filename) {
      let splitName = filename.split('.');
      splitName.pop();
      return splitName.join('.');
    },

    waitForSelectedOptions() {
      return new Promise(resolve => {
        this.eventEmitter.once('selected-options', () => {
          resolve();
        });
      });
    },

    async downloadDocumentByLink(url, name) {
      let response = await this.$http.get(url, { responseType: 'arraybuffer' });
      let blob = new Blob([response.data], {
        type: this.get(response.headers, 'content-type'),
      });
      let link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = name;
      link.click();
    },

    async checkDocumentProcessed(doc) {
      // This to avoid hanging the browser
      await this.sleep(1000);

      const done = this.currentQuestionnaire.extracted_documents.some(d => {
        return d.source.includes(doc.document_name);
      });

      if (!done) {
        await this.checkDocumentProcessed(doc);
      }
    },
  },
};
